import cn from 'classnames'
import Image from 'next/image'
import {FC, PropsWithChildren} from 'react'

import {HtmlElementType, Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {Link} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Button} from './Button'
import {ButtonVariant} from './ButtonTypes'

export enum ContentCardImageSize {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

const IMAGE_SIZE_SMALL = '32px'
const IMAGE_SIZE_MEDIUM = '44px'
const IMAGE_SIZE_LARGE = '60px'

const getImageSize = (size: ContentCardImageSize) => {
  switch (size) {
    case ContentCardImageSize.small:
      return IMAGE_SIZE_SMALL
    case ContentCardImageSize.medium:
      return IMAGE_SIZE_MEDIUM
    default:
      return IMAGE_SIZE_LARGE
  }
}

type CardProps = {
  title: string
  titleTextVariant?: TextVariant
  titleElement?: HtmlElementType
  imageSrc?: string
  imageAlt?: string
  imageSize?: ContentCardImageSize
  description?: string
  descriptionTextVariant?: TextVariant
  buttonText?: string
  fullWidth?: boolean
  isHoverable?: boolean
}

const Card: FC<PropsWithChildren<CardProps>> = ({
  title,
  titleTextVariant = TextVariant.text16,
  titleElement = 'h3',
  imageSrc,
  imageAlt,
  imageSize = ContentCardImageSize.medium,
  description,
  descriptionTextVariant = TextVariant.text14,
  buttonText,
  fullWidth = false,
  isHoverable = false,
  children,
}) => (
  <>
    <div className={cn('card', fullWidth && 'full-width', isHoverable && 'hoverable')}>
      <div>
        {imageSrc && imageAlt && (
          <div className="image">
            <Image
              src={imageSrc}
              alt={imageAlt}
              style={{objectFit: 'contain', objectPosition: fullWidth ? 'center' : 'left'}}
              fill
              sizes="84px"
            />
          </div>
        )}
      </div>
      <div className="content">
        <Text variant={titleTextVariant} element={titleElement} fontWeight={TextFontWeight.bold}>
          {title}
        </Text>
        {description && (
          <div className="description">
            <Text variant={descriptionTextVariant} color={theme.palette.text.light}>
              {description}
            </Text>
          </div>
        )}
        {buttonText && (
          <div className="button-wrapper">
            <Button type="button" variant={ButtonVariant.primary}>
              {buttonText}
            </Button>
          </div>
        )}
        {children}
      </div>
    </div>

    <style jsx>{`
      .card {
        width: 100%;
        height: 100%;
        background: ${theme.palette.background.default};
        box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
        border-radius: 12px;
        transition: box-shadow ${HOVER_TRANSITION}ms ease-in-out;
        padding: ${theme.spacing[4]};
      }
      .card.hoverable:hover {
        box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]};
      }
      .image {
        position: relative;
        width: 100%;
        max-width: 84px;
        height: ${getImageSize(imageSize)};
        margin-bottom: ${theme.spacing[3]};
      }
      .description {
        margin-top: ${theme.spacing[2]};
      }
      .button-wrapper {
        margin-top: ${theme.spacing[4]};
      }
      .full-width {
        display: flex;
        padding: ${theme.spacing[6]};
      }
      .full-width .image {
        width: 120px;
        max-width: inherit;
        height: 120px;
      }
      .full-width .content {
        margin-left: ${theme.spacing[6]};
      }
      @media ${TABLET_SCREENSIZE} {
        .full-width {
          padding: ${theme.spacing[5]};
        }
        .full-width .content {
          margin-left: ${theme.spacing[5]};
        }
      }
      @media ${MOBILE_SCREENSIZE} {
        .full-width {
          display: block;
          padding: ${theme.spacing[4]};
        }
        .full-width .image {
          width: 64px;
          height: 64px;
        }
        .full-width .content {
          margin-left: 0;
          margin-top: ${theme.spacing[4]};
        }
      }
    `}</style>
  </>
)

type ContentCardProps = CardProps & {
  linkHref?: string
  prefetch?: boolean
  trackedEvent?: {
    name: string
    parameters?: any
  }
  openInNewTab?: boolean
}

export const ContentCard: FC<PropsWithChildren<ContentCardProps>> = ({
  title,
  titleTextVariant,
  titleElement,
  imageSrc,
  imageAlt,
  imageSize,
  description,
  descriptionTextVariant,
  linkHref,
  prefetch,
  trackedEvent,
  buttonText,
  openInNewTab = false,
  fullWidth = false,
  children,
}) => (
  <>
    {linkHref ? (
      <Link href={linkHref} prefetch={prefetch} legacyBehavior>
        <a
          onClick={() =>
            trackedEvent && analytics.track(trackedEvent.name, trackedEvent.parameters)
          }
          role="link"
          tabIndex={0}
          target={openInNewTab ? '_blank' : '_self'}
          rel={openInNewTab ? 'noopener noreferrer' : ''}
        >
          <Card
            title={title}
            titleTextVariant={titleTextVariant}
            titleElement={titleElement}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
            imageSize={imageSize}
            description={description}
            descriptionTextVariant={descriptionTextVariant}
            buttonText={buttonText}
            fullWidth={fullWidth}
            isHoverable
          >
            {children}
          </Card>
        </a>
      </Link>
    ) : (
      <Card
        title={title}
        titleTextVariant={titleTextVariant}
        titleElement={titleElement}
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        imageSize={imageSize}
        description={description}
        descriptionTextVariant={descriptionTextVariant}
        buttonText={buttonText}
        fullWidth={fullWidth}
      >
        {children}
      </Card>
    )}
  </>
)
